import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Spin, Row, Col, Tooltip } from 'antd';
import { isNil } from 'lodash';
import cx from 'classnames';

import { getFullName } from 'utils/personUtils';
import { getLocation } from 'utils/locationUtils';
import BaseAlert from 'components/wrappers/BaseAlert';
import BaseLabel from 'components/wrappers/BaseLabel';
import { ApiErrorMessage } from 'constants/ErrorMessages';
import {
  getPartnerStatusLabel,
  getPartnerStatusHint,
} from 'utils/partnerUtils';
import BasePageCard from 'components/wrappers/BasePageCard';
import BasePageLabel from 'components/wrappers/BasePageLabel';
import FlagCheckbox from 'components/FlagCheckbox';
import { PartnerResponseModel } from 'api/backofficeApiClient';
import {
  getPartnerDetails,
  updatePartnerIsIcp,
} from 'redux/reducers/partnerSlice';
import {
  isAdminSelector,
  isRecruiterCompanySelector,
} from 'redux/selectors/authenticationSelectors';
import { useAppDispatch } from 'hooks/useAppDispatch';
import { useAppSelector } from 'hooks/useAppSelector';

import styles from './PartnersDetailsPage.module.less';
import { getFormattedNumber } from '../../../utils/numberUtils';

type ParamsProps = {
  partnerId: string;
};

export default function PartnerDetailsPage() {
  const { partnerId } = useParams<ParamsProps>();
  const dispatch = useAppDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const [partnerDetails, setPartnerDetails] = useState<PartnerResponseModel>();
  const isAdminRole = useAppSelector(isAdminSelector);
  const isRecruiterCompany = useAppSelector(isRecruiterCompanySelector);

  const loadPartnerDetails = useCallback(async () => {
    if (!partnerId) return;

    setIsLoading(true);
    const result = await dispatch(
      getPartnerDetails({ partnerId, isAdminRole }),
    );

    if (getPartnerDetails.fulfilled.match(result)) {
      setPartnerDetails(result.payload);
    } else {
      BaseAlert.error(result.error.message || ApiErrorMessage);
    }

    setIsLoading(false);
  }, [dispatch, partnerId, isAdminRole]);

  useEffect(() => {
    loadPartnerDetails();
  }, [loadPartnerDetails]);

  // Local update instead of a reload: loadPartnerDetails swaps the whole page for a spinner.
  const handleIsIcpChange = async (value: boolean) => {
    if (!partnerId) return;

    const result = await dispatch(
      updatePartnerIsIcp({ partnerId, isIcp: value }),
    );

    if (updatePartnerIsIcp.fulfilled.match(result)) {
      setPartnerDetails((prev) => prev && { ...prev, isIcp: value });
    } else {
      BaseAlert.error(result.error.message || ApiErrorMessage);
    }
  };

  if (isLoading) return <Spin size="large" />;

  if (!partnerDetails) return null;

  const { generalInfo, primaryContact, partnerStatus, creator, isIcp } =
    partnerDetails;

  const personToContactFullName = getFullName(
    primaryContact?.firstName,
    primaryContact?.lastName,
  );

  const correctLocation = getLocation(
    generalInfo?.country,
    generalInfo?.address,
    generalInfo?.city || generalInfo?.internationalCityName,
    generalInfo?.state || generalInfo?.internationalStateName,
    generalInfo?.zipCode,
  );

  return (
    <div className="page-wrapper scroll-box">
      <div className="page-holder">
        <BasePageLabel label="Partner details" creator={creator} />

        <div className="page-box page-inner">
          <div className="page-inner-container">
            <div className="page-head">
              <div className={cx('page-head__left', styles.headLeft)}>
                <div className="page-title page-head__title">
                  <div>{generalInfo?.companyName}</div>
                  {isAdminRole && (
                    <FlagCheckbox
                      label="Is ICP"
                      checked={isIcp}
                      onChange={handleIsIcpChange}
                    />
                  )}
                </div>
              </div>
              {!isNil(partnerStatus) && (
                <div className="page-head__label">
                  <BaseLabel status={getPartnerStatusLabel(partnerStatus)} />
                  {isRecruiterCompany && (
                    <Tooltip
                      title={getPartnerStatusHint(partnerStatus)}
                      placement="right"
                      trigger="click"
                      overlayClassName={styles.tooltip}
                    >
                      <span
                        className={cx(
                          'icon icon-attention-filled',
                          styles.icon,
                        )}
                      />
                    </Tooltip>
                  )}
                </div>
              )}
            </div>

            <Row gutter={[10, 10]}>
              <Col xs={24} md={12} className="d-flex">
                <BasePageCard
                  title="General information"
                  className="stretch-card"
                  icons={<span className="icon icon-writing-tool-user" />}
                >
                  <div className="content-wrapper">
                    <Row gutter={[4, 4]} className="titled-sheet">
                      {!isNil(generalInfo?.subcontractorRate) && (
                        <Col xs={24} md={12}>
                          <div className="title">Subcontractor rate:</div>
                          <div>
                            $
                            {getFormattedNumber(
                              generalInfo?.subcontractorRate as number,
                              { separator: ',', toFixed: 2 },
                            )}
                          </div>
                        </Col>
                      )}

                      {correctLocation && (
                        <Col xs={24} md={12}>
                          <div className="title">Location:</div>
                          <div>{correctLocation}</div>
                        </Col>
                      )}

                      <Col xs={24} md={12}>
                        <div className="title">
                          Registered on the TalentCloud:
                        </div>
                        <div>
                          {generalInfo?.isRegisteredOnMarketplace
                            ? 'Yes'
                            : 'No'}
                        </div>
                      </Col>

                      <Col xs={24} md={12}>
                        <div className="title">Number of assignments:</div>
                        <div>{generalInfo?.assignmentsCount || 0}</div>
                      </Col>
                    </Row>
                  </div>
                </BasePageCard>
              </Col>

              <Col xs={24} md={12} className="d-flex">
                <BasePageCard
                  title="Person to contact"
                  className="stretch-card"
                  icons={<span className="icon icon-paper" />}
                >
                  <div className="content-wrapper">
                    <Row gutter={[4, 4]} className="titled-sheet">
                      {personToContactFullName && (
                        <Col xs={24} md={12}>
                          <div className="title">Name:</div>
                          <div>{personToContactFullName}</div>
                        </Col>
                      )}

                      {!isNil(primaryContact?.email) && (
                        <Col xs={24} md={12}>
                          <div className="title">Email:</div>
                          <div>{primaryContact?.email}</div>
                        </Col>
                      )}

                      {!isNil(primaryContact?.phoneNumber) && (
                        <Col xs={24} md={12}>
                          <div className="title">Phone number:</div>
                          <div>{primaryContact?.phoneNumber}</div>
                        </Col>
                      )}
                    </Row>
                  </div>
                </BasePageCard>
              </Col>
            </Row>
          </div>
        </div>
      </div>
    </div>
  );
}
